import { useQueryUser } from '@heidi/workspace/data';

import { getComplimentaryDaysLeft } from '../../data/query/complimentary-trial-end.utils';
import { isComplimentaryCreditAllowance } from '../../data/query/credit-allowance.utils';
import { useQueryCreditAllowance } from '../../data/query/useQueryCreditAllowance';
import { useQueryUserBillingInfo } from '../../data/query/useQueryUserBillingInfo';
import { resolveYearlyMaxComplimentaryDiscount } from '../../data/yearly-max-complimentary-discount';
import { isHeidi2OptInCreditUsageEnabled } from './is-credit-usage-surface-enabled';
import { isMaxBillingSeat } from './is-max-billing-seat';

export type ComplimentaryYearlyMaxOffer = {
  cycleEnd: string;
  /** Catalog discount to send when the checkout target is yearly Max. */
  applyDiscountForYearlyMax: ReturnType<typeof resolveYearlyMaxComplimentaryDiscount>;
};

/**
 * Yearly Max complimentary offer while the uncapped Heidi 2 cycle is still
 * live. Broader than the T-7 countdown: the coupon and seat-page banner stay
 * available for the whole complimentary window. Free-trial members stay on the
 * trial countdown banner instead — this offer is complimentary-cycle only.
 * Max seats already hold the target plan, so they never see the upsell.
 */
export function useComplimentaryYearlyMaxOffer({
  isHeidi2OptInEnabled,
  isTrialEndEnabled,
  isTrialing,
}: {
  isHeidi2OptInEnabled: boolean;
  isTrialEndEnabled: boolean;
  /** Reverse / fake clinician / evidence free trial — excludes the 15% offer. */
  isTrialing: boolean;
}): ComplimentaryYearlyMaxOffer | null {
  const { data: user } = useQueryUser();
  const { data: billingInfo } = useQueryUserBillingInfo();
  const isCohortEnabled = isHeidi2OptInCreditUsageEnabled({
    isHeidi2OptInEnabled,
    heidiVersion: billingInfo?.heidi_version,
  });
  const { data: allowance } = useQueryCreditAllowance({
    hasUser: !!user,
    enabled:
      isTrialEndEnabled &&
      isHeidi2OptInEnabled &&
      !isTrialing &&
      !user?.is_managed &&
      !isMaxBillingSeat(billingInfo),
  });

  if (
    !isTrialEndEnabled ||
    !isCohortEnabled ||
    isTrialing ||
    user?.is_managed ||
    isMaxBillingSeat(billingInfo)
  ) {
    return null;
  }
  if (allowance == null || !isComplimentaryCreditAllowance(allowance)) {
    return null;
  }
  if (getComplimentaryDaysLeft(allowance, new Date()) == null) {
    return null;
  }

  return {
    cycleEnd: allowance.cycle_end,
    applyDiscountForYearlyMax: resolveYearlyMaxComplimentaryDiscount({
      isTrialEndEnabled: true,
      isComplimentaryUsage: true,
      isYearlyMaxTarget: true,
    }),
  };
}
